(() => {
  if (window.DOBundleAvailability) return;

  const CART_MAX_AGE_MS = 1500;
  const CHANGE_EVENT = 'bundle:availability:change';

  class BundleAvailabilityManager {
    constructor() {
      this.cart = null;
      this.cartFetchedAt = 0;
      this.cartPromise = null;
      this.variantInventory = new Map();
      this.reservedQuantities = {};
      this.notifyFrame = null;

      this.onCartAdd = this.onCartAdd.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
    }

    get root() {
      return window.Shopify?.routes?.root ?? '/';
    }

    addVariantQuantity(target, variantId, quantity) {
      if (!variantId) return;

      const key = String(variantId);
      const numericQuantity = Number(quantity);
      if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) return;

      target[key] = (target[key] || 0) + numericQuantity;
    }

    parseBundleSelection(value) {
      if (!value) return [];

      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    addLineReservations(target, item, quantity = item?.quantity) {
      const lineQuantity = Number(quantity) || 0;
      if (!item || lineQuantity <= 0) return;

      this.addVariantQuantity(target, item.variant_id, lineQuantity);

      const selection = this.parseBundleSelection(item.properties?._bundle_selection);
      selection.forEach((row) => {
        this.addVariantQuantity(target, row?.variantId, lineQuantity);
      });
    }

    buildReservedQuantities(cart = this.cart, options = {}) {
      const reserved = {};
      const excludedLineKey = options.excludeLineKey ? String(options.excludeLineKey) : '';

      (cart?.items || []).forEach((item) => {
        if (excludedLineKey && String(item.key) === excludedLineKey) return;
        this.addLineReservations(reserved, item);
      });

      return reserved;
    }

    normalizeVariant(rawVariant) {
      if (!rawVariant?.id) return null;

      const inventoryQuantity = Number(rawVariant.inventory_quantity);

      return {
        id: String(rawVariant.id),
        available: rawVariant.available == null ? null : Boolean(rawVariant.available),
        inventory_quantity: Number.isFinite(inventoryQuantity) ? inventoryQuantity : null,
        inventory_policy: rawVariant.inventory_policy ?? null,
        inventory_management: rawVariant.inventory_management ?? null,
      };
    }

    registerVariants(variants) {
      if (!Array.isArray(variants)) return;

      let changed = false;

      variants.forEach((rawVariant) => {
        const variant = this.normalizeVariant(rawVariant);
        if (!variant) return;

        const current = this.variantInventory.get(variant.id) || {};
        const next = {
          ...current,
          ...variant,
          available: variant.available == null ? current.available : variant.available,
          inventory_quantity: variant.inventory_quantity == null ? current.inventory_quantity : variant.inventory_quantity,
          inventory_policy: variant.inventory_policy ?? current.inventory_policy ?? null,
          inventory_management: variant.inventory_management ?? current.inventory_management ?? null,
        };

        this.variantInventory.set(variant.id, next);
        changed = true;
      });

      if (changed) this.scheduleNotify();
    }

    registerVariantsFromJson(value) {
      if (!value?.trim()) return;

      try {
        const parsed = JSON.parse(value);
        this.registerVariants(Array.isArray(parsed) ? parsed : Object.values(parsed));
      } catch {}
    }

    getReservedQuantities() {
      return { ...this.reservedQuantities };
    }

    getVariantInventoryLimit(variant) {
      if (!variant) return null;
      if (!variant.inventory_management) return null;
      if (variant.inventory_policy === 'continue') return null;

      const quantity = Number(variant.inventory_quantity);
      if (Number.isFinite(quantity)) return Math.max(0, quantity);

      return variant.available === false ? 0 : null;
    }

    canReserveVariant(variantId, quantity, reservedQuantities = this.reservedQuantities) {
      const variant = this.variantInventory.get(String(variantId));
      if (!variant) return { valid: true };

      if (variant.available === false) {
        return { valid: false, reason: 'unavailable', variant };
      }

      const limit = this.getVariantInventoryLimit(variant);
      if (limit == null) return { valid: true, variant };

      const requested = Number(quantity) || 0;
      const alreadyReserved = Number(reservedQuantities[String(variantId)] || 0);
      if (alreadyReserved + requested > limit) {
        return { valid: false, reason: 'quantity', variant, limit, requested, alreadyReserved };
      }

      return { valid: true, variant };
    }

    validateReservations(requiredQuantities, reservedQuantities) {
      const failures = [];

      Object.entries(requiredQuantities).forEach(([variantId, quantity]) => {
        const result = this.canReserveVariant(variantId, quantity, reservedQuantities);
        if (!result.valid) failures.push({ variantId, ...result });
      });

      return {
        valid: failures.length === 0,
        failures,
      };
    }

    async validateBundleSelection(variantIds, quantity = 1) {
      const cart = await this.getCart({ maxAge: CART_MAX_AGE_MS });
      const required = {};
      const bundleQuantity = Number(quantity) || 1;

      (variantIds || []).forEach((variantId) => {
        this.addVariantQuantity(required, variantId, bundleQuantity);
      });

      return this.validateReservations(required, this.buildReservedQuantities(cart));
    }

    async validateLineQuantityChange(lineKey, quantity) {
      const cart = await this.getCart({ maxAge: CART_MAX_AGE_MS });
      const line = (cart?.items || []).find((item) => String(item.key) === String(lineKey));
      if (!line) return { valid: true, failures: [] };

      const nextQuantity = Number(quantity) || 0;
      const currentQuantity = Number(line.quantity) || 0;
      if (nextQuantity <= currentQuantity) return { valid: true, failures: [] };

      const required = {};
      this.addLineReservations(required, line, nextQuantity);

      return this.validateReservations(required, this.buildReservedQuantities(cart, { excludeLineKey: lineKey }));
    }

    setCart(cart) {
      if (!cart?.items) return;

      this.cart = cart;
      this.cartFetchedAt = Date.now();
      this.reservedQuantities = this.buildReservedQuantities(cart);
      this.scheduleNotify();
    }

    async getCart(options = {}) {
      const maxAge = options.maxAge ?? CART_MAX_AGE_MS;
      const isFresh = this.cart && Date.now() - this.cartFetchedAt <= maxAge;

      if (!options.force && isFresh) return this.cart;
      if (this.cartPromise) return this.cartPromise;

      this.cartPromise = fetch(`${this.root}cart.js`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Cache-Control': 'no-cache',
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Cart request failed');
          return response.json();
        })
        .then((cart) => {
          this.setCart(cart);
          return cart;
        })
        .finally(() => {
          this.cartPromise = null;
        });

      return this.cartPromise;
    }

    refreshCart() {
      return this.getCart({ force: true });
    }

    scheduleNotify() {
      if (this.notifyFrame) cancelAnimationFrame(this.notifyFrame);

      this.notifyFrame = requestAnimationFrame(() => {
        this.notifyFrame = null;
        window.dispatchEvent(
          new CustomEvent(CHANGE_EVENT, {
            detail: {
              cart: this.cart,
              reservedQuantities: this.getReservedQuantities(),
            },
          })
        );
      });
    }

    onCartAdd() {
      this.refreshCart().catch(() => {});
    }

    onCartChange(event) {
      const cart = event.detail?.cart;
      if (cart?.items) {
        this.setCart(cart);
        return;
      }

      this.refreshCart().catch(() => {});
    }

    connect() {
      window.addEventListener('cart:add', this.onCartAdd);
      window.addEventListener('cart:change', this.onCartChange);
      this.getCart().catch(() => {});
    }
  }

  const manager = new BundleAvailabilityManager();
  window.DOBundleAvailability = manager;
  manager.connect();
})();